import { omit } from 'lodash';
import axios from 'axios';

const namespaced = true;
let source = axios.CancelToken.source();

const state = {
  itemFolders: [],
  itemFiles: [],
  progressUpload: 0
};

const getters = {
  folders: state => state.itemFolders,
  files: state => state.itemFiles,
  progressUpload: state => state.progressUpload
};

const mutations = {
  SET_FOLDERS: (state, payload) => {
    state.itemFolders = payload;
  },
  SET_FILES: (state, payload) => {
    state.itemFiles = payload;
  },
  SET_PROGRESS: (state, progress) => {
    state.progressUpload = progress;
  },
  CLEAR_FOLDERS: state => {
    state.itemFolders = [];
  },
  CLEAR_FILES: state => {
    state.itemFiles = [];
  },
  DONE_PROGRESS: state => {
    state.progressUpload = 100;
  },
  CLEAR_PROGRESS: state => {
    state.progressUpload = 0;
  }
};

const actions = {
  async fetchFolders({ commit, rootGetters }, { classId, courseId, search, classSectionId }) {
    try {
      let url;
      const url1 =
        rootGetters['Auth/currentRole'] === 4 ? `/api/folder/class-section/${classSectionId}` : `/api/folder-student/class-section/${classSectionId}`;
      const url2 =
        rootGetters['Auth/currentRole'] === 4
          ? `/api/file/class/${classId}/course/${courseId}`
          : `/api/file/student/class/${classId}/course/${courseId}`;

      if (classSectionId) {
        url = url1;
      } else {
        url = url2;
      }
      const {
        data: { data }
      } = await window.axios.get(url, { params: { search, sortBy: 'folderId' } });
      commit('SET_FOLDERS', data);
      return Promise.resolve(data);
    } catch (err) {
      throw err;
    }
  },

  async createFolder({ rootGetters }, { classId, courseId, folderName, classSectionId }) {
    try {
      let url;
      if (!classSectionId) {
        url = `/api/file/class/${classId}/course/${courseId}`;
      } else {
        url = `/api/folder/class-section/${classSectionId}`;
      }
      const {
        data: { data }
      } = await window.axios.post(`${url}`, {
        folderName,
        schoolId: rootGetters['Auth/currentSchool']
      });
      return Promise.resolve(data);
    } catch (err) {
      throw err;
    }
  },

  updateFolder({ rootGetters }, payload) {
    const schoolId = rootGetters['Auth/currentSchool'];
    return window.axios.put(`api/file/folder/${payload.folderId}`, { ...payload, schoolId });
  },

  // eslint-disable-next-line no-unused-vars
  removeFolder({ commit }, folderId) {
    return window.axios.delete(`api/file/folder/${folderId}`);
  },

  // eslint-disable-next-line no-unused-vars
  updateTree({ commit }, payload) {
    return window.axios.put(`api/file`, payload);
  },

  async fetchFiles({ commit, rootGetters }, payload) {
    try {
      const defaultPageable = rootGetters['table/defaultPageable'];
      const url = rootGetters['Auth/currentRole'] === 4 ? `/api/file/folder/${payload.folderId}` : `/api/file/student/folder/${payload.folderId}`;
      const mapping = {
        title: 'fileName'
      };
      const sortBy = payload?.sortBy?.[0] ? `${mapping[payload.sortBy?.[0]] ?? payload.sortBy?.[0]}|${payload.sortDesc?.[0] ? 'desc' : 'asc'}` : null;
      const params = {
        size: defaultPageable.limit,
        page: defaultPageable.currentPage - 1,
        search: defaultPageable.search,
        sortBy
      };
      const {
        data: { data }
      } = await window.axios.get(url, { params });
      commit('SET_FILES', data);
      return Promise.resolve(data);
    } catch (err) {
      throw err;
    }
  },

  // eslint-disable-next-line no-unused-vars
  async fetchFile({ commit }, fileId) {
    try {
      const {
        data: { data }
      } = window.axios.get(`api/file/${fileId}`);
      return Promise.resolve(data);
    } catch (err) {
      throw err;
    }
  },

  updateFile({ rootGetters }, payload) {
    const { id } = payload;
    return window.axios.put(`api/file/${id.replace('File-', '')}`, { ...payload, schoolId: rootGetters['Auth/currentSchool'] });
  },

  // eslint-disable-next-line no-unused-vars
  removeFile({ commit }, fileId) {
    return window.axios.delete(`api/file/${fileId}`);
  },

  async upload({ rootGetters, commit }, payload) {
    try {
      const fd = new FormData();
      Object.keys(omit(payload, ['folderId'])).forEach(p => fd.append(p, payload[p]));
      fd.append('schoolId', rootGetters['Auth/currentSchool']);
      const result = await window.axios.post(`api/file/folder/${payload.folderId}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress(progressEvent) {
          commit('SET_PROGRESS', Math.round((progressEvent.loaded * 97) / progressEvent.total));
        },
        cancelToken: source.token
      });
      return Promise.resolve(result);
    } catch (err) {
      throw err;
    }
  },

  // eslint-disable-next-line no-unused-vars
  cancelUpload({ commit }) {
    source.cancel('Operation canceled by the user.');
    source = axios.CancelToken.source();
  }
};

export default { namespaced, state, getters, mutations, actions };
